import { Alert } from '@ordo/ui/alert'
import { Badge } from '@ordo/ui/badge'
import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@ordo/ui/card'
import { Checkbox } from '@ordo/ui/checkbox'
import { Input } from '@ordo/ui/input'
import { RadioGroup, RadioGroupItem } from '@ordo/ui/radio-group'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ordo/ui/select'
import { Separator } from '@ordo/ui/separator'
import { Textarea } from '@ordo/ui/textarea'
import { AsyncCombobox } from 'compositions/AsyncCombobox'
import { ConfirmDialog } from 'compositions/ConfirmDialog'
import { Page } from 'compositions/Page'
import { SenderSelector } from 'compositions/SenderSelector'
import { useContact, useLayout, usePreviewTransactional, useSendTransactional, useTemplate } from 'hooks/api'
import { useAsyncLoader } from 'hooks/useAsyncLoader'
import { getApiErrorMessage } from 'hooks/useFetchApi'
import { AlertCircle, ArrowLeft, ArrowRight, CheckCircle, Send } from 'lucide-react'
import { type Recipient, RecipientSelector } from 'pages/communications/recipient-selector'
import { TemplatePreview } from 'pages/templates/template-preview'
import {
  buildVariablesRecord,
  DEFAULT_STATE,
  TemplateVariables,
  type TemplateVariableState,
} from 'pages/templates/template-variables'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router'
import type { CommunicationMethod } from 'types/communications'
import type { SendRecord } from 'types/sends'
import type { Template } from 'types/templates'
import { formatPhone } from 'utils/phone'

type Step = 'compose' | 'review' | 'result'

/**
 * Where the message body comes from. `template` uses a saved Template (and
 * may override its body for this send only); `compose` sends an ad-hoc body
 * with no template at all.
 */
type ContentMode = 'template' | 'compose'

/* ── Compose Step ── */

function ComposeStep({
  method,
  setMethod,
  contentMode,
  setContentMode,
  templateId,
  setTemplateId,
  subject,
  setSubject,
  body,
  setBody,
  editTemplateInline,
  setEditTemplateInline,
  senderId,
  setSenderId,
  recipients,
  onAddRecipient,
  onRemoveRecipient,
  eventState,
  setEventState,
  templateBody,
  layoutBody,
  variablesRecord,
  onNext,
}: {
  method: CommunicationMethod
  setMethod: (m: CommunicationMethod) => void
  contentMode: ContentMode
  setContentMode: (m: ContentMode) => void
  templateId: string
  setTemplateId: (id: string) => void
  subject: string
  setSubject: (s: string) => void
  body: string
  setBody: (s: string) => void
  editTemplateInline: boolean
  setEditTemplateInline: (v: boolean) => void
  senderId: string
  setSenderId: (id: string) => void
  recipients: Recipient[]
  onAddRecipient: (r: Recipient) => void
  onRemoveRecipient: (r: Recipient) => void
  eventState: TemplateVariableState
  setEventState: (s: TemplateVariableState) => void
  templateBody: string
  layoutBody: string | undefined
  variablesRecord: Record<string, unknown>
  onNext: () => void
}) {
  const loadTemplates = useAsyncLoader<Template>('/api/templates', { active: true })
  const { data: selectedTemplate } = useTemplate(templateId || undefined)
  const selectedTemplateItem = useMemo<Template | null>(
    () => (templateId && selectedTemplate?.id === templateId ? selectedTemplate : null),
    [templateId, selectedTemplate],
  )
  // SendGrid rejects emails without a subject — gating the Review step
  // here keeps the failure visible at compose time rather than as an
  // opaque per-recipient dispatch error after the operator commits.
  const subjectValid = method !== 'EMAIL' || subject.trim().length > 0
  // Either a template or a composed body will do — the server enforces the
  // same rule, this just surfaces it before the operator commits.
  const hasContent = contentMode === 'template' ? !!templateId : body.trim().length > 0
  const canProceed = hasContent && recipients.length > 0 && subjectValid

  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
      <Card className="min-w-0 flex-1">
        <CardHeader>
          <CardTitle>Compose</CardTitle>
        </CardHeader>
        <Separator />
        <CardContent className="space-y-4 pt-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Method */}
            <div className="space-y-1.5">
              <span className="text-sm font-medium text-muted-foreground">Delivery Method</span>
              <Select
                value={method}
                onValueChange={(v) => {
                  setMethod(v as CommunicationMethod)
                  setSenderId('')
                }}>
                <SelectTrigger className="w-full text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="EMAIL">Email</SelectItem>
                  <SelectItem value="SMS">SMS</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Template — only when sending from one */}
            {contentMode === 'template' && (
              <div className="space-y-1.5">
                <span className="text-sm font-medium text-muted-foreground">Template</span>
                <AsyncCombobox<Template>
                  value={selectedTemplateItem}
                  onChange={(item) => setTemplateId(item?.id ?? '')}
                  load={loadTemplates}
                  getKey={(item) => item.id}
                  getLabel={(item) => item.name}
                  placeholder="Select a template"
                  searchPlaceholder="Search templates…"
                  emptyMessage="No templates available"
                />
              </div>
            )}
          </div>

          {/* Content source. Growth needs one-off variations without minting
              a new Template for every tweak — that's the whole point here. */}
          <div className="space-y-1.5">
            <span className="text-sm font-medium text-muted-foreground">Content</span>
            <RadioGroup
              value={contentMode}
              onValueChange={(v) => setContentMode(v as ContentMode)}
              className="flex flex-wrap gap-x-6 gap-y-2">
              <div className="flex items-center gap-2">
                <RadioGroupItem value="template" id="contentModeTemplate" />
                <label htmlFor="contentModeTemplate" className="text-sm">
                  Use template
                </label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="compose" id="contentModeCompose" />
                <label htmlFor="contentModeCompose" className="text-sm">
                  Compose
                </label>
              </div>
            </RadioGroup>
          </div>

          {/* Inline template edit — one send only, never writes back. */}
          {contentMode === 'template' && templateId && (
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="editTemplateInline"
                  checked={editTemplateInline}
                  onCheckedChange={(checked) => setEditTemplateInline(checked === true)}
                />
                <label htmlFor="editTemplateInline" className="text-sm">
                  Edit content for this send only
                </label>
              </div>
              {editTemplateInline && (
                <>
                  <p className="text-xs text-muted-foreground">
                    Changes here go out with this send. The saved template is not modified.
                  </p>
                  <Textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    rows={12}
                    className="font-mono text-xs"
                    aria-label="Message body for this send"
                  />
                </>
              )}
            </div>
          )}

          {/* Composed body — no template at all. */}
          {contentMode === 'compose' && (
            <div className="space-y-1.5">
              <label htmlFor="composeBody" className="text-sm font-medium text-muted-foreground">
                Body <span className="text-destructive">*</span>
              </label>
              <Textarea
                id="composeBody"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={12}
                className="font-mono text-xs"
                placeholder="Write your message. Liquid variables like {{ contact.firstName }} work here."
                required
                aria-required="true"
              />
            </div>
          )}

          {/* Saved senders are filtered by channel. */}
          <SenderSelector method={method} value={senderId} onChange={setSenderId} />

          {/* Subject — EMAIL only, required */}
          {method === 'EMAIL' && (
            <div className="space-y-1.5">
              <label htmlFor="sendSubject" className="text-sm font-medium text-muted-foreground">
                Subject <span className="text-destructive">*</span>
              </label>
              <Input
                id="sendSubject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                required
                aria-required="true"
              />
            </div>
          )}

          {/* Recipients */}
          <div className="space-y-1.5">
            <span className="text-sm font-medium text-muted-foreground">Recipients</span>
            <RecipientSelector recipients={recipients} onAdd={onAddRecipient} onRemove={onRemoveRecipient} />
          </div>

          <div className="flex justify-end pt-2">
            <Button onClick={onNext} disabled={!canProceed}>
              Review
              <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Preview + Variables */}
      <div className="flex min-w-0 flex-1 flex-col gap-4">
        <TemplatePreview body={templateBody} mode="html" variables={variablesRecord} layoutBody={layoutBody} />
        <TemplateVariables state={eventState} onChange={setEventState} templateBody={templateBody} />
      </div>
    </div>
  )
}

/* ── Review Step ── */

function ReviewStep({
  method,
  subject,
  templateName,
  recipients,
  sending,
  onBack,
  onSend,
}: {
  method: CommunicationMethod
  subject: string
  templateName: string
  recipients: Recipient[]
  sending: boolean
  onBack: () => void
  onSend: () => void
}) {
  const contactRecipients = recipients.filter((r) => r.type === 'contact')
  const groupRecipients = recipients.filter((r) => r.type === 'group')

  // Resolve groups + dynamic rules + dedup so the count reflects actual
  // recipients, not raw selector entries (1 group ≠ 1 person).
  const { perform: previewSend, loading: previewLoading } = usePreviewTransactional()
  const [previewCount, setPreviewCount] = useState<number | null>(null)
  const [previewError, setPreviewError] = useState<string | null>(null)

  useEffect(() => {
    const to = contactRecipients
      .map((r) => (method === 'EMAIL' ? { email: r.email ?? undefined } : { phoneNumber: r.phoneNumber ?? undefined }))
      .filter((entry) => entry.email || entry.phoneNumber)
    const contactGroupIds = groupRecipients.map((r) => r.id)
    if (to.length === 0 && contactGroupIds.length === 0) {
      setPreviewCount(0)
      return
    }
    let cancelled = false
    setPreviewError(null)
    previewSend({
      method,
      to: to.length > 0 ? to : undefined,
      contactGroupIds: contactGroupIds.length > 0 ? contactGroupIds : undefined,
    })
      .then((result) => {
        if (!cancelled) {
          setPreviewCount(result.totalRecipients)
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setPreviewError(err instanceof Error ? err.message : 'Failed to compute recipient count')
        }
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [method, JSON.stringify(contactRecipients.map((r) => r.id)), JSON.stringify(groupRecipients.map((r) => r.id))])

  const resolvedCount = previewCount ?? recipients.length

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
        <CardTitle>Review &amp; Send</CardTitle>
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onBack} disabled={sending}>
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back
          </Button>
          <ConfirmDialog
            title="Send communication"
            description={`This will send to ${resolvedCount} recipient${resolvedCount !== 1 ? 's' : ''} immediately. This cannot be undone.`}
            confirmLabel="Send"
            onConfirm={onSend}
            trigger={
              <Button size="sm" disabled={sending || previewLoading}>
                <Send className="mr-1 h-4 w-4" />
                {sending ? 'Sending...' : 'Send Now'}
              </Button>
            }
          />
        </div>
      </CardHeader>
      <Separator />
      <CardContent className="space-y-6 pt-6">
        {/* Summary */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="space-y-0.5">
            <dt className="text-sm font-medium text-muted-foreground">Method</dt>
            <dd className="text-sm">{method}</dd>
          </div>
          <div className="space-y-0.5">
            <dt className="text-sm font-medium text-muted-foreground">Template</dt>
            <dd className="text-sm">{templateName}</dd>
          </div>
          {method === 'EMAIL' && (
            <div className="space-y-0.5">
              <dt className="text-sm font-medium text-muted-foreground">Subject</dt>
              <dd className="text-sm">{subject || <span className="text-muted-foreground">—</span>}</dd>
            </div>
          )}
        </div>

        {previewError ? <p className="text-sm text-destructive">{previewError}</p> : null}

        <Separator />

        {/* Recipients */}
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-muted-foreground">
            Recipients ({previewLoading ? '…' : resolvedCount})
          </h3>

          {groupRecipients.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Groups</p>
              <div className="flex flex-wrap gap-1.5">
                {groupRecipients.map((r) => (
                  <Badge key={r.id} variant="secondary">
                    {r.label}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {contactRecipients.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Contacts</p>
              <div className="overflow-x-auto rounded-md border border-border">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Name</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                        {method === 'EMAIL' ? 'Email' : 'Phone'}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {contactRecipients.map((r) => {
                      const address = method === 'EMAIL' ? r.email : formatPhone(r.phoneNumber ?? null)
                      return (
                        <tr key={r.id} className="border-b border-border last:border-0">
                          <td className="px-3 py-1.5 text-xs">{r.label}</td>
                          <td className="px-3 py-1.5 text-xs">
                            {address || <span className="text-muted-foreground/50">—</span>}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

/* ── Result Step ── */

function ResultStep({ result, onReset }: { result: SendRecord; onReset: () => void }) {
  const isSuccess = result.status === 'SENT'
  const isPartial = result.status === 'PARTIAL'

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <CheckCircle
            className={`h-5 w-5 ${isSuccess ? 'text-green-500' : isPartial ? 'text-yellow-500' : 'text-destructive'}`}
          />
          <CardTitle>{isSuccess ? 'Sent Successfully' : isPartial ? 'Partially Sent' : 'Send Failed'}</CardTitle>
        </div>
      </CardHeader>
      <Separator />
      <CardContent className="space-y-4 pt-6">
        <div className="flex flex-wrap gap-2">
          <Badge variant="secondary">
            {result.recipientCount} recipient{result.recipientCount !== 1 ? 's' : ''}
          </Badge>
          <Badge variant="secondary" className="bg-green-900/30 text-green-400">
            {result.successCount} delivered
          </Badge>
          {result.failureCount > 0 && (
            <Badge variant="secondary" className="bg-red-900/30 text-red-400">
              {result.failureCount} failed
            </Badge>
          )}
        </div>

        {result.error && <p className="text-sm text-destructive">{result.error}</p>}

        <Separator />

        <div className="flex items-center gap-2">
          <Button asChild>
            <Link to="/communications">View Messages</Link>
          </Button>
          <Button variant="outline" onClick={onReset}>
            Send Another
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

/* ── Main Page ── */

export function SendPage() {
  const { perform: sendTransactional, loading: sending } = useSendTransactional()

  const [step, setStep] = useState<Step>('compose')
  const [method, setMethod] = useState<CommunicationMethod>('EMAIL')
  const [contentMode, setContentMode] = useState<ContentMode>('template')
  const [templateId, setTemplateId] = useState('')
  const [body, setBody] = useState('')
  const [editTemplateInline, setEditTemplateInline] = useState(false)
  const [subject, setSubject] = useState('')
  // Tracks whether the operator manually edited the subject — template
  // switches keep suggesting until they take over.
  const subjectEditedRef = useRef(false)
  const [senderId, setSenderId] = useState('')
  const [recipients, setRecipients] = useState<Recipient[]>([])
  const [eventState, setEventState] = useState<TemplateVariableState>(DEFAULT_STATE)
  const [result, setResult] = useState<SendRecord | null>(null)
  const [error, setError] = useState<string | null>(null)

  const { data: templateDetail } = useTemplate(templateId || undefined)
  // Prefill the subject from the template's default when the operator
  // hasn't typed one — the server would apply the same fallback, but
  // showing it here keeps the Review step honest about what goes out.
  useEffect(() => {
    if (templateDetail?.id === templateId && templateDetail.subject && !subjectEditedRef.current) {
      setSubject(templateDetail.subject)
    }
  }, [templateDetail, templateId])
  const { data: layoutDetail } = useLayout(templateDetail?.layout?.id ?? undefined)
  const layoutBody = templateDetail?.layout?.id ? templateDetail.layout.body || layoutDetail?.body : undefined

  // Seed the inline editor with the template's current body the first time
  // it's opened, so "edit for this send only" starts from the real content
  // rather than a blank box.
  useEffect(() => {
    if (editTemplateInline && !body && templateDetail?.id === templateId && templateDetail.body) {
      setBody(templateDetail.body)
    }
  }, [editTemplateInline, body, templateDetail, templateId])

  // What actually goes out — the preview must show the composed body when
  // there is one, not the untouched template.
  const effectiveBody = contentMode === 'compose' || editTemplateInline ? body : (templateDetail?.body ?? '')

  const { data: previewContact } = useContact(eventState.contactId ?? undefined)
  const variablesRecord = useMemo(
    () => buildVariablesRecord(eventState, previewContact ?? null),
    [eventState, previewContact],
  )

  const handleAddRecipient = useCallback((r: Recipient) => {
    setRecipients((prev) => [...prev, r])
  }, [])

  const handleRemoveRecipient = useCallback((r: Recipient) => {
    setRecipients((prev) => prev.filter((p) => !(p.type === r.type && p.id === r.id)))
  }, [])

  const handleSend = useCallback(async () => {
    setError(null)
    try {
      const to = recipients
        .filter((r) => r.type === 'contact')
        .map((r) =>
          method === 'EMAIL' ? { email: r.email ?? undefined } : { phoneNumber: r.phoneNumber ?? undefined },
        )
        .filter((entry) => entry.email || entry.phoneNumber)
      const contactGroupIds = recipients.filter((r) => r.type === 'group').map((r) => r.id)

      const eventData: Record<string, unknown> = {}
      for (const v of eventState.eventVariables) {
        if (v.key.trim() && v.value) {
          eventData[v.key.trim()] = v.value
        }
      }

      // Compose mode sends no template at all. Template mode sends a body
      // only when the operator opted into editing it for this send — that's
      // what keeps the saved template untouched.
      const sendTemplateId = contentMode === 'template' ? templateId : undefined
      const sendBody = contentMode === 'compose' || editTemplateInline ? body.trim() || undefined : undefined

      const res = await sendTransactional({
        templateId: sendTemplateId,
        body: sendBody,
        method,
        subject: method === 'EMAIL' ? subject : undefined,
        senderId: senderId || undefined,
        to: to.length > 0 ? to : undefined,
        contactGroupIds: contactGroupIds.length > 0 ? contactGroupIds : undefined,
        eventData: Object.keys(eventData).length > 0 ? eventData : undefined,
      })
      setResult(res)
      setStep('result')
    } catch (err) {
      setError(getApiErrorMessage(err, 'Send failed'))
    }
  }, [
    recipients,
    eventState,
    contentMode,
    templateId,
    body,
    editTemplateInline,
    method,
    subject,
    senderId,
    sendTransactional,
  ])

  const handleReset = useCallback(() => {
    setStep('compose')
    setMethod('EMAIL')
    setContentMode('template')
    setTemplateId('')
    setBody('')
    setEditTemplateInline(false)
    setSubject('')
    subjectEditedRef.current = false
    setSenderId('')
    setRecipients([])
    setEventState(DEFAULT_STATE)
    setResult(null)
    setError(null)
  }, [])

  return (
    <Page>
      <div className="mb-4">
        <h1 className="text-xl font-semibold">Send Communication</h1>
        <p className="text-sm text-muted-foreground">
          Compose and send a one-off communication to selected recipients.
        </p>
      </div>

      {error && (
        <Alert variant="error" className="mb-4 flex items-start gap-3">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="space-y-1">
            <p className="font-medium">Send failed</p>
            <p className="text-sm opacity-90">{error}</p>
          </div>
        </Alert>
      )}

      {step === 'compose' && (
        <ComposeStep
          method={method}
          setMethod={setMethod}
          contentMode={contentMode}
          setContentMode={setContentMode}
          templateId={templateId}
          setTemplateId={setTemplateId}
          body={body}
          setBody={setBody}
          editTemplateInline={editTemplateInline}
          setEditTemplateInline={setEditTemplateInline}
          subject={subject}
          setSubject={(value) => {
            subjectEditedRef.current = true
            setSubject(value)
          }}
          senderId={senderId}
          setSenderId={setSenderId}
          recipients={recipients}
          onAddRecipient={handleAddRecipient}
          onRemoveRecipient={handleRemoveRecipient}
          eventState={eventState}
          setEventState={setEventState}
          templateBody={effectiveBody}
          layoutBody={layoutBody}
          variablesRecord={variablesRecord}
          onNext={() => setStep('review')}
        />
      )}

      {step === 'review' && (
        <ReviewStep
          method={method}
          subject={subject}
          templateName={
            contentMode === 'compose'
              ? 'Composed message'
              : editTemplateInline
                ? `${templateDetail?.name ?? ''} (edited for this send)`
                : (templateDetail?.name ?? '')
          }
          recipients={recipients}
          sending={sending}
          onBack={() => setStep('compose')}
          onSend={handleSend}
        />
      )}

      {step === 'result' && result && <ResultStep result={result} onReset={handleReset} />}
    </Page>
  )
}
